import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Descriptions, Tag, Button, Space, Spin, Typography, Modal, message, Table, Card,
} from 'antd';
import { EditOutlined, DeleteOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import { getQuote, deleteQuote, listQuoteLineItems, type Quote, type QuoteLineItem } from '@/api/quoteApi';
import { getCustomer } from '@/api/customerApi';
import { getContact } from '@/api/contactApi';
import { getOpportunity } from '@/api/opportunityApi';
import { useAuth } from '@/hooks/useAuth';

const { Title } = Typography;

const STATUS_COLORS: Record<string, string> = {
  draft: 'default',
  sent: 'processing',
  accepted: 'success',
  rejected: 'error',
  expired: 'warning',
};

export default function QuoteDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();

  const [quote, setQuote] = useState<Quote | null>(null);
  const [lineItems, setLineItems] = useState<QuoteLineItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [resolvedNames, setResolvedNames] = useState<Record<string, string>>({});

  const canWrite = hasPermission('quotes', 'write');
  const canDelete = hasPermission('quotes', 'delete');

  const loadData = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const [q, items] = await Promise.allSettled([
        getQuote(id),
        listQuoteLineItems(id),
      ]);

      if (q.status === 'fulfilled') {
        setQuote(q.value);

        // Resolve entity names
        const names: Record<string, string> = {};
        const resolvers: Promise<void>[] = [];
        if (q.value.customer_id) {
          resolvers.push(
            getCustomer(q.value.customer_id)
              .then((c) => { names.customer = c.name ?? q.value.customer_id; })
              .catch(() => { names.customer = q.value.customer_id; })
          );
        }
        if (q.value.contact_id) {
          resolvers.push(
            getContact(q.value.contact_id)
              .then((c) => { names.contact = c.name ?? q.value.contact_id; })
              .catch(() => { names.contact = q.value.contact_id; })
          );
        }
        if (q.value.opportunity_id) {
          resolvers.push(
            getOpportunity(q.value.opportunity_id)
              .then((o) => { names.opportunity = o.name ?? q.value.opportunity_id; })
              .catch(() => { names.opportunity = q.value.opportunity_id; })
          );
        }
        await Promise.allSettled(resolvers);
        setResolvedNames(names);
      } else {
        setError(q.reason?.message || t('app.not_found'));
      }

      if (items.status === 'fulfilled') {
        setLineItems(items.value);
      }
    } catch {
      setError(t('app.error'));
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleDelete = useCallback(() => {
    if (!id) return;
    Modal.confirm({
      title: t('app.confirm'),
      content: t('quotes.confirm_delete'),
      okText: t('app.yes'),
      cancelText: t('app.no'),
      okType: 'danger',
      onOk: async () => {
        try {
          await deleteQuote(id);
          message.success(t('quotes.delete_success'));
          navigate('/quotes');
        } catch (err) {
          message.error(err instanceof Error ? err.message : t('app.error'));
        }
      },
    });
  }, [id, t, navigate]);

  if (loading) return <Spin size="large" />;
  if (error || !quote) {
    return (
      <div>
        <Typography.Text type="danger">{error || t('app.not_found')}</Typography.Text>
        <br />
        <Button onClick={() => navigate('/quotes')}>{t('app.back')}</Button>
      </div>
    );
  }

  const lineItemColumns: ColumnsType<QuoteLineItem> = [
    {
      title: '#',
      dataIndex: 'sort_order',
      key: 'sort_order',
      width: 60,
    },
    {
      title: t('quotes.line_item.product_name'),
      dataIndex: 'product_name',
      key: 'product_name',
      ellipsis: true,
    },
    {
      title: t('quotes.line_item.description'),
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      render: (val: string) => val || '—',
    },
    {
      title: t('quotes.line_item.quantity'),
      dataIndex: 'quantity',
      key: 'quantity',
      width: 100,
      align: 'right',
    },
    {
      title: t('quotes.line_item.unit_price'),
      dataIndex: 'unit_price',
      key: 'unit_price',
      width: 120,
      align: 'right',
      render: (val: number) => val?.toLocaleString(undefined, { minimumFractionDigits: 2 }),
    },
    {
      title: t('quotes.line_item.discount'),
      dataIndex: 'discount_percentage',
      key: 'discount_percentage',
      width: 100,
      align: 'right',
      render: (val: number) => val > 0 ? `${val}%` : '—',
    },
    {
      title: t('quotes.line_item.line_total'),
      dataIndex: 'line_total',
      key: 'line_total',
      width: 130,
      align: 'right',
      render: (val: number) => val?.toLocaleString(undefined, { minimumFractionDigits: 2 }),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/quotes')}>
            {t('app.back')}
          </Button>
          <Title level={4} style={{ margin: 0 }}>{quote.title}</Title>
        </Space>
        <Space>
          {canWrite && (
            <Button
              type="primary"
              icon={<EditOutlined />}
              onClick={() => navigate(`/quotes/${id}/edit`)}
            >
              {t('app.edit')}
            </Button>
          )}
          {canDelete && (
            <Button danger icon={<DeleteOutlined />} onClick={handleDelete}>
              {t('app.delete')}
            </Button>
          )}
        </Space>
      </div>

      <Descriptions bordered column={2} size="small">
        <Descriptions.Item label={t('quotes.field.quote_number')}>
          {quote.quote_number || '—'}
        </Descriptions.Item>
        <Descriptions.Item label={t('quotes.field.status')}>
          <Tag color={STATUS_COLORS[quote.status] ?? 'default'}>
            {t(`quotes.status_options.${quote.status}`)}
          </Tag>
        </Descriptions.Item>
        <Descriptions.Item label={t('quotes.field.title')} span={2}>
          {quote.title}
        </Descriptions.Item>
        <Descriptions.Item label={t('quotes.field.description')} span={2}>
          {quote.description || '—'}
        </Descriptions.Item>
        <Descriptions.Item label={t('quotes.field.customer_id')}>
          {resolvedNames.customer || quote.customer_id}
        </Descriptions.Item>
        <Descriptions.Item label={t('quotes.field.contact_id')}>
          {quote.contact_id ? (resolvedNames.contact || quote.contact_id) : '—'}
        </Descriptions.Item>
        <Descriptions.Item label={t('quotes.field.opportunity_id')}>
          {quote.opportunity_id ? (resolvedNames.opportunity || quote.opportunity_id) : '—'}
        </Descriptions.Item>
        <Descriptions.Item label={t('quotes.field.valid_until')}>
          {quote.valid_until ? new Date(quote.valid_until).toLocaleDateString() : '—'}
        </Descriptions.Item>
        <Descriptions.Item label={t('quotes.field.currency')}>
          {quote.currency}
        </Descriptions.Item>
        <Descriptions.Item label={t('quotes.field.payment_terms')}>
          {quote.payment_terms || '—'}
        </Descriptions.Item>
        <Descriptions.Item label={t('quotes.field.notes')} span={2}>
          {quote.notes || '—'}
        </Descriptions.Item>
        <Descriptions.Item label={t('quotes.field.tags')} span={2}>
          {(quote.tags ?? []).length > 0
            ? quote.tags.map((tag) => <Tag key={tag}>{tag}</Tag>)
            : '—'}
        </Descriptions.Item>
      </Descriptions>

      <Card
        title={t('quotes.line_items_title')}
        style={{ marginTop: 16 }}
        size="small"
      >
        <Table<QuoteLineItem>
          dataSource={lineItems}
          columns={lineItemColumns}
          rowKey="id"
          pagination={false}
          size="small"
          summary={() => (
            <Table.Summary fixed>
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={6} align="right">
                  <strong>{t('quotes.field.subtotal')}</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={1} align="right">
                  <strong>
                    {quote.subtotal?.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </strong>
                </Table.Summary.Cell>
              </Table.Summary.Row>
              {(quote.discount_percentage > 0 || quote.discount_amount > 0) && (
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0} colSpan={6} align="right">
                    {t('quotes.field.discount')}
                    {quote.discount_percentage > 0 ? ` (${quote.discount_percentage}%)` : ''}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={1} align="right">
                    -{(quote.discount_amount + quote.subtotal * quote.discount_percentage / 100)
                      .toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              )}
              {quote.tax_percentage > 0 && (
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0} colSpan={6} align="right">
                    {t('quotes.field.tax')} ({quote.tax_percentage}%)
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={1} align="right">
                    {quote.tax_amount?.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              )}
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={6} align="right">
                  <strong>{t('quotes.field.total')}</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={1} align="right">
                  <strong>
                    {quote.currency}{' '}
                    {quote.total?.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </strong>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            </Table.Summary>
          )}
        />
      </Card>

      <Descriptions bordered column={2} size="small" style={{ marginTop: 16 }}>
        <Descriptions.Item label={t('quotes.field.created_by')}>
          {quote.created_by}
        </Descriptions.Item>
        <Descriptions.Item label={t('quotes.field.created_at')}>
          {new Date(quote.created_at).toLocaleString()}
        </Descriptions.Item>
        <Descriptions.Item label={t('quotes.field.updated_by')}>
          {quote.updated_by}
        </Descriptions.Item>
        <Descriptions.Item label={t('quotes.field.updated_at')}>
          {new Date(quote.updated_at).toLocaleString()}
        </Descriptions.Item>
        <Descriptions.Item label={t('quotes.field.branch_id')}>
          {quote.branch_id}
        </Descriptions.Item>
      </Descriptions>
    </div>
  );
}
